<script setup lang="ts">
import type { AdminCustomerResponse } from "mws-finance-ts-sdk";
import type { AdminUser } from "mws-identity-ts-sdk-v2-admin";
import { computed } from "vue";

import { useGetUserCognitoQuery } from "@/users/api/queries";

definePage({
  meta: {
    auth: ["Users.ViewCognito"],
  },
});

const props = defineProps<{
  identityUser: AdminUser | undefined;
  customer: AdminCustomerResponse | undefined;
}>();

const userId = computed(() => props.identityUser?.id);
const isQueryEnabled = computed(() => !!userId.value);
const { data, isPending, isError, error } = useGetUserCognitoQuery(userId, {
  enabled: isQueryEnabled,
});

const formatDate = (date: string | Date | undefined | null) => {
  if (!date) return "—";

  return new Date(date).toISOString();
};
</script>

<template>
  <div class="space-y-4">
    <div class="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm">
      <strong>Dev tool.</strong>
      Raw Cognito user records linked to this customer's email. Multiple records
      appear when external identity providers (Google, Apple, Facebook…) are
      linked.
    </div>

    <ContentLoader
      v-if="isPending"
      viewBox="0 0 200 200"
    />

    <div
      v-else-if="isError"
      class="rounded-md bg-red-100 text-red-800 p-3 text-sm"
    >
      Failed to load Cognito users: {{ error?.message ?? "unknown error" }}
    </div>

    <template v-else-if="data">
      <div class="text-sm text-gray-700">
        <div>
          <strong>Email queried:</strong>
          {{ data.email }}
        </div>
        <div>
          <strong>User pool:</strong>
          {{ data.userPoolId }}
        </div>
        <div>
          <strong>Records found:</strong>
          {{ data.users?.length ?? 0 }}
        </div>
      </div>

      <div
        v-if="!data.users?.length"
        class="rounded-md bg-gray-100 p-3 text-sm"
      >
        No Cognito users found for this email.
      </div>

      <div
        v-for="(cognitoUser, idx) in data.users ?? []"
        :key="cognitoUser.username ?? idx"
        class="rounded-md border border-gray-200 bg-white p-4"
      >
        <h3 class="font-mono text-sm font-bold mb-3">
          {{ cognitoUser.username }}
        </h3>

        <table class="w-full text-sm">
          <tbody>
            <tr>
              <td class="font-medium pr-4 align-top w-56">User status</td>
              <td>{{ cognitoUser.userStatus ?? "—" }}</td>
            </tr>
            <tr>
              <td class="font-medium pr-4 align-top">Enabled</td>
              <td>{{ cognitoUser.enabled }}</td>
            </tr>
            <tr>
              <td class="font-medium pr-4 align-top">Created</td>
              <td>{{ formatDate(cognitoUser.userCreateDate) }}</td>
            </tr>
            <tr>
              <td class="font-medium pr-4 align-top">Last modified</td>
              <td>{{ formatDate(cognitoUser.userLastModifiedDate) }}</td>
            </tr>
            <tr>
              <td class="font-medium pr-4 align-top">Preferred MFA</td>
              <td>{{ cognitoUser.preferredMfaSetting ?? "—" }}</td>
            </tr>
            <tr>
              <td class="font-medium pr-4 align-top">MFA settings</td>
              <td>
                {{
                  cognitoUser.userMFASettingList?.length
                    ? cognitoUser.userMFASettingList.join(", ")
                    : "—"
                }}
              </td>
            </tr>
          </tbody>
        </table>

        <h4 class="font-medium text-sm mt-4 mb-1">Attributes</h4>
        <table class="w-full text-sm">
          <tbody>
            <tr
              v-for="attr in cognitoUser.attributes ?? []"
              :key="attr.name"
              class="border-t border-gray-100"
            >
              <td class="font-mono text-xs pr-4 align-top w-56 py-1">
                {{ attr.name }}
              </td>
              <td class="font-mono text-xs break-all py-1">
                {{ attr.value ?? "" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
